import React, { useEffect, useRef, Component } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Recommendation, CATEGORY_CONFIG } from './data';
interface MapViewProps {
  items: Recommendation[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}
// Hand-drawn doodle style SVG icons for map markers
const getIconSvg = (category: string) => {
  if (category === 'EATS') {
    // Doodle fork & knife
    return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M4 2.5c.2 0 .1 3.2.3 5 .2 1.5 1.2 2.8 2.8 2.8s2.5-1.2 2.7-2.8c.2-1.8.1-5 .3-5" />
      <path d="M7.1 10.3c-.1 2 0 5.5.1 7.2.1 1.5-.1 4.2-.1 4.2" />
      <path d="M16.5 2.2c-.3.5-1.2 2-1.5 4-.4 2.5.2 5 1.8 6l.7.3" />
      <path d="M17.5 12.5c.1 1.8-.2 5-.1 7 .1 1.5 0 2.3 0 2.3" />
      <path d="M17.8 2.3c-.1 2.5-.2 5.5.2 7.5.2 1.2.8 2.2.8 2.2" />
    </svg>`;
  }
  if (category === 'WALKS') {
    // Doodle tree
    return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M12 22c.2 0 .1-3 0-5-.1-1.5.1-2 .1-2" />
      <path d="M7.5 15.5c-1.5-.8-3-2.5-2.5-4.2.4-1.5 2-1.8 2-1.8s-.8-1.5-.3-3c.5-1.3 2-1.8 2-1.8S9.2 3 10.5 2.2c1.2-.7 2.8-.2 3.5.5.8-.3 2-.2 2.8.5.7.7.8 1.8.5 2.5.8.5 1.5 1.5 1.5 2.8 0 1.5-1 2.5-1 2.5s1.2.8 1.2 2.5c0 1.8-1.5 3-3 3.5" />
      <path d="M8 15c.5.3 1.5.8 4 .8s3.5-.5 4-.8" />
    </svg>`;
  }
  // Doodle map pin
  return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M12 21.5c-.5-.5-7-6.5-7-11.8C5 5.5 8 2.2 12 2.2s7 3.3 7 7.5c0 5.3-6.5 11.3-7 11.8Z" />
    <circle cx="12" cy="9.5" r="2.5" />
    <path d="M10.2 8.8c.3-.5 1-.8 1.8-.8" />
  </svg>`;
};
// Component to handle map flying and zooming
function MapController({
  selectedId,
  items
}: {
  selectedId: number | null;
  items: Recommendation[];
}) {
  const map = useMap();
  useEffect(() => {
    if (selectedId) {
      const item = items.find(i => i.id === selectedId);
      if (item) {
        // Fly to the marker with a slight offset so the popup is visible
        map.flyTo([item.lat + 0.002, item.lng], 15, {
          duration: 1.5,
          easeLinearity: 0.25
        });
      }
    } else if (items.length > 0) {
      // If deselected, fit bounds to show all items
      const bounds = L.latLngBounds(items.map(i => [i.lat, i.lng]));
      map.fitBounds(bounds, {
        padding: [30, 30],
        maxZoom: 14
      });
    }
  }, [selectedId, items, map]);
  return null;
}
export function MapView({
  items,
  selectedId,
  onSelect
}: MapViewProps) {
  const markerRefs = useRef<{
    [key: number]: L.Marker | null;
  }>({});
  // Open popup programmatically when selected from outside
  useEffect(() => {
    if (selectedId && markerRefs.current[selectedId]) {
      const marker = markerRefs.current[selectedId];
      if (marker && !marker.isPopupOpen()) {
        marker.openPopup();
      }
    }
  }, [selectedId]);
  return <MapContainer center={[45.41, -75.71]} zoom={14} className="w-full h-full z-0" zoomControl={false} data-id="element-49">
      <TileLayer attribution='&copy; <a href="https://carto.com/attributions">CARTO</a>' url="https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" data-id="element-50" />

      <MapController selectedId={selectedId} items={items} data-id="element-51" />

      {items.map(item => {
      const config = CATEGORY_CONFIG[item.category];
      const isSelected = selectedId === item.id;
      const customIcon = L.divIcon({
        className: 'custom-map-marker',
        html: `
            <div class="marker-pin ${isSelected ? 'selected' : ''}" style="background-color: ${config.color}; color: white;">
              ${getIconSvg(item.category)}
            </div>
          `,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
        popupAnchor: [0, -20]
      });
      return <Marker key={item.id} position={[item.lat, item.lng]} icon={customIcon} ref={ref => {
        markerRefs.current[item.id] = ref;
      }} eventHandlers={{
        click: () => onSelect(item.id)
      }} data-id="element-52">
            <Popup className="recommendation-popup" closeButton={false} data-id="element-53">
              <div className="flex flex-col min-w-[200px]" data-id="element-54">
                <div className="flex items-center gap-2 mb-1" data-id="element-55">
                  <span className="text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded" style={{
                backgroundColor: config.bg,
                color: config.color
              }} data-id="element-56">
                    {config.label}
                  </span>
                  <span className="text-[10px] text-gray-400 font-medium" data-id="element-57">
                    {item.neighborhood}
                  </span>
                </div>
                <h3 className="font-semibold text-gray-900 text-base mb-1" data-id="element-58">
                  {item.name}
                </h3>
                <p className="text-gray-500 text-xs italic line-clamp-2" data-id="element-59">
                  {item.description}
                </p>
              </div>
            </Popup>
          </Marker>;
    })}
    </MapContainer>;
}